/* eslint-disable react/jsx-props-no-spreading */
import React, { forwardRef, HTMLAttributes, ReactElement } from 'react';
import { Author, Image, Video } from '../../types/puppyGuide';
import useReducedMotion from '../../hooks/useReducedMotion';
import { imgPath } from './constants';
import Banner from './Banner';
import VideoPlayer from './VideoPlayer';

import './introduction-section.scss';

interface IntroductionSectionProps extends HTMLAttributes<HTMLElement> {
  title: string;
  sectionNumber: number;
  video: Video;
  sectionHeader: string;
  author: Author;
  content: ReactElement | null;
  backgroundColor: string;
  topOffset: number;
  reducedMotionImage: Image;
  headerStickyStartLocationRef: React.MutableRefObject<HTMLDivElement | null> | null;
}

const IntroductionSection = forwardRef<HTMLDivElement, IntroductionSectionProps>(
  (
    {
      title,
      sectionNumber,
      video,
      reducedMotionImage,
      sectionHeader,
      author,
      content,
      backgroundColor,
      topOffset,
      headerStickyStartLocationRef = null,
      ...props
    },
    ref,
  ) => {
    const isAutoPlay = useReducedMotion(true, false);
    return (
      <>
        <div
          className="puppy-guide-introduction-section__anchor"
          style={{ bottom: topOffset }}
          ref={ref}
        />
        <div
          className="puppy-guide-introduction-section"
          data-testid={`puppy-guide-section-${sectionNumber}`}
          id={`section-${sectionNumber}`}
          {...props}
        >
          <VideoPlayer
            baseClass="puppy-guide-introduction-section__graphics-container"
            videoClass="puppy-guide-introduction-section__graphics"
            isAutoPlay={isAutoPlay}
            videoAttributes={video}
          />
          <div className="puppy-guide-introduction-section__banner-title-container">
            <Banner className="puppy-guide-introduction-section__banner" />
            <h3
              className="puppy-guide-introduction-section__title"
              style={{ backgroundColor: backgroundColor }}
              ref={headerStickyStartLocationRef}
            >
              {sectionNumber}. {title}
            </h3>
          </div>
          <div className="starter-guide-container puppy-guide-introduction-section__content">
            <h4 className="puppy-guide-introduction-section__header">{sectionHeader}</h4>
            <div className="puppy-guide-introduction-section__details">{content}</div>
            <div className="puppy-guide-author">
              <img
                className="puppy-guide-author__image"
                src={`${imgPath}${author.image.src}`}
                alt={author.image.alt}
              />
              <div className="puppy-guide-author__content">
                <div>{author.introBy}</div>
                <div className="puppy-guide-author__name">{author.name}</div>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  },
);

IntroductionSection.displayName = 'IntroductionSection';

export default IntroductionSection;
